// TrinityView.jsx — Matrix v2 Trinity (anti-fraud, anti-strata, ranný report)
// MVP: zoznam alertov z /api/live/alerts. Rozdelené krit/varovania/info.
import React, { useState, useEffect, useMemo } from 'react'
import useLiveRefresh from '../hooks/useLiveRefresh.js'
import { useNavigate } from 'react-router-dom'
import { COLORS, TYPO, SPACE, RADIUS, SK, todayLocal } from '../theme.js'
import { PageHeader, FilterChips, KpiRow, KpiCard, Tabs, SearchInput } from '../components/Ui.jsx'
import { Btn, BtnAdd, BtnGhost } from '../components/Btn.jsx'
import { Modal } from '../components/Modal.jsx'
import { useToast } from '../components/Toast.jsx'
import { fetchAuth } from '../auth/fetchAuth.js'
import { useAuth } from '../auth/AuthContext.jsx'
import HelpHint from '../components/HelpHint.jsx'
import NavIcon from '../components/NavIcon.jsx'

const ROLES_TRINITY = ['admin','owner','owner_view','office_manager','accountant']

const LEVEL = {
  critical: { color: COLORS.remove, bg: 'rgba(239,68,68,0.06)', label: 'Kritické' },
  warning:  { color: '#f59e0b',     bg: 'rgba(245,158,11,0.06)', label: 'Varovanie' },
  info:     { color: COLORS.nav,    bg: 'rgba(59,130,246,0.06)', label: 'Info' },
}

export default function TrinityView() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [alerts, setAlerts] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all')
  const [trinityTab, setTrinityTab] = useState('asistent')
  const [phmCash, setPhmCash] = useState(null)

  useEffect(() => {
    if (!user || !ROLES_TRINITY.includes(user.role)) return
    fetchAuth('/api/live/alerts')
      .then(r => r.ok ? r.json() : { alerts: [] })
      .then(d => setAlerts(d.alerts || []))
      .catch(() => {})
      .finally(() => setLoading(false))
    fetchAuth('/api/stats/phm-cash').then(r => r.ok ? r.json() : null).then(setPhmCash).catch(() => {})
  }, [user])
  useLiveRefresh(() => { if (user && ROLES_TRINITY.includes(user.role)) { fetchAuth('/api/live/alerts').then(r => r.ok ? r.json() : { alerts: [] }).then(d => setAlerts(d.alerts || [])).catch(() => {}); fetchAuth('/api/stats/phm-cash').then(r => r.ok ? r.json() : null).then(setPhmCash).catch(() => {}) } }) // živé

  const filtered = useMemo(() => {
    if (filter === 'all') return alerts
    return alerts.filter(a => a.level === filter)
  }, [alerts, filter])

  const kpi = useMemo(() => ({
    critical: alerts.filter(a => a.level === 'critical').length,
    warning:  alerts.filter(a => a.level === 'warning').length,
    info:     alerts.filter(a => a.level === 'info').length,
    total:    alerts.length,
  }), [alerts])

  if (!user || !ROLES_TRINITY.includes(user.role)) {
    return <div style={{ padding: SPACE.xl, color: COLORS.muted }}>
      Nemáš oprávnenie zobraziť Trinity.
    </div>
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <PageHeader title="Trinity" subtitle="Anti-fraud · anti-strata · ranný report" />
      <div style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: SPACE.lg }}>
        <Tabs value={trinityTab} onChange={setTrinityTab} tabs={[
          { key: 'asistent', label: 'Trinity asistent' },
          { key: 'ulohy', label: 'Úlohy' },
          { key: 'alerty', label: 'Čo sledujem' },
          { key: 'emaily', label: 'Emaily' },
        ]} />
        {trinityTab === 'asistent' ? <AntonFrame /> : trinityTab === 'ulohy' ? <TasksTab /> : trinityTab === 'emaily' ? <EmailTab /> : (<>
        <CashReconPanel />
        <KpiRow cols={4}>
          <KpiCard title="Kritické"  value={String(kpi.critical)} color={COLORS.remove} highlight={kpi.critical > 0} />
          <KpiCard title="Varovania" value={String(kpi.warning)}  color="#f59e0b" />
          <KpiCard title="Info"      value={String(kpi.info)}     color={COLORS.nav} />
          <KpiCard title="Spolu"        value={String(kpi.total)}    color={COLORS.muted} />
        </KpiRow>

        {phmCash && (
          <div style={{ display: 'flex', alignItems: 'center', gap: SPACE.lg, padding: '10px 16px', background: 'rgba(245,158,11,0.10)', border: '1px solid #f59e0b', borderLeft: '5px solid #f59e0b', borderRadius: RADIUS.md, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, color: '#b45309', textTransform: 'uppercase', fontSize: TYPO.sizes.xs, letterSpacing: '0.05em' }}>PHM v hotovosti</span>
            <span>Tento rok: <b style={{ fontFamily: TYPO.mono }}>{SK.eur(phmCash.thisYear)}</b></span>
            <span>Tento mesiac: <b style={{ fontFamily: TYPO.mono }}>{SK.eur(phmCash.thisMonth)}</b></span>
            <span style={{ color: COLORS.muted, fontSize: TYPO.sizes.sm }}>Spolu: {SK.eur(phmCash.total)} · {phmCash.count} dokladov</span>
            <HelpHint section="87.3" text="ČO: koľko PHM (pohonných hmôt) sa platí HOTOVOSŤOU. PREČO: PHM cash = najčastejší zdroj únikov, preto stála kontrola. KAM: súčet z pokladne + bločkov platených hotovosťou." />
          </div>
        )}

        <FilterChips
          options={[
            { key: 'all',      label: 'Všetko',     color: COLORS.muted },
            { key: 'critical', label: 'Kritické',   color: COLORS.remove },
            { key: 'warning',  label: 'Varovania',  color: '#f59e0b' },
            { key: 'info',     label: 'Info',       color: COLORS.nav },
          ]}
          value={filter} onChange={setFilter} />

        {loading ? (
          <div style={{ padding: SPACE.xl, color: COLORS.muted, textAlign: 'center' }}>Načítavam Trinity alerty…</div>
        ) : filtered.length === 0 ? (
          <div style={{
            padding: SPACE.xl, color: COLORS.add, textAlign: 'center',
            background: 'rgba(16,185,129,0.06)',
            border: `1px solid ${COLORS.add}`, borderRadius: RADIUS.lg,
            fontSize: TYPO.sizes.md, fontWeight: TYPO.weights.semibold,
          }}>
            Žiadne alerty pre tento filter. Všetko v poriadku.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.sm }}>
            {filtered.map((a, i) => {
              const cfg = LEVEL[a.level] || LEVEL.info
              return (
                <div key={i} style={{
                  background: cfg.bg, border: `1px solid ${cfg.color}`,
                  borderLeft: `4px solid ${cfg.color}`, borderRadius: RADIUS.md,
                  padding: SPACE.md, display: 'flex', gap: SPACE.md, alignItems: 'flex-start',
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', gap: SPACE.sm, alignItems: 'center', marginBottom: 2 }}>
                      <span style={{
                        fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.bold,
                        color: cfg.color, textTransform: 'uppercase', letterSpacing: '0.06em',
                      }}>{cfg.label}</span>
                      {a.category && <span style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted }}>· {a.category}</span>}
                    </div>
                    <div style={{ fontSize: TYPO.sizes.md, fontWeight: TYPO.weights.bold, color: COLORS.text, marginBottom: 4 }}>
                      {a.title}
                    </div>
                    <div style={{ fontSize: TYPO.sizes.sm, color: COLORS.text, lineHeight: 1.4 }}>
                      {a.message}
                    </div>
                  </div>
                  {a.action && (
                    <button onClick={() => navigate(a.action.route)}
                      style={{
                        background: cfg.color, color: '#fff', border: 'none',
                        borderRadius: RADIUS.sm, padding: '6px 12px', cursor: 'pointer',
                        fontFamily: 'inherit', fontWeight: TYPO.weights.semibold, fontSize: TYPO.sizes.sm,
                        whiteSpace: 'nowrap',
                      }}>{a.action.label} →</button>
                  )}
                </div>
              )
            })}
          </div>
        )}
        </>)}
      </div>
    </div>
  )
}

// ── EmailTab — Trinity klasifikované emaily ──────────────────────────
function EmailTab() {
  const [emails, setEmails] = useState([])
  const [loading, setLoading] = useState(true)
  const [showAdd, setShowAdd] = useState(false)
  const toast = useToast()
  function nacitaj() {
    setLoading(true)
    fetchAuth('/api/trinity/emails').then(r => r.ok ? r.json() : [])
      .then(d => setEmails(Array.isArray(d) ? d : []))
      .finally(() => setLoading(false))
  }
  useEffect(nacitaj, [])
  async function archive(id) {
    await fetchAuth(`/api/trinity/emails/${id}/status`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: 'archived' }),
    })
    nacitaj()
  }
  const PRIO_COLORS = {
    critical: COLORS.remove, high: '#f59e0b', normal: COLORS.nav, low: COLORS.muted,
  }
  const KAT_LABELS = {
    faktura: 'Faktúra', cenova_ponuka: 'Cenová ponuka', reklamacia: 'Reklamácia',
    objednavka: 'Objednávka', spam: 'Spam', osobne: 'Osobné', ine: 'Iné',
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.md, paddingTop: SPACE.md }}>
      <div style={{
        padding: SPACE.md, background: 'rgba(59,130,246,0.06)',
        border: `1px solid ${COLORS.nav}`, borderRadius: RADIUS.md, fontSize: TYPO.sizes.sm,
      }}>
        <strong style={{ color: COLORS.nav }}>Trinity Email klasifikátor</strong> — Claude AI roztriedi prilepené emaily
        (Faktúra / Cenová ponuka / Reklamácia / Objednávka / Spam / Osobné / Iné) + priorita.
        IMAP automatický feed = nasledujúca fáza (v2 cez n8n).
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <BtnAdd onClick={() => setShowAdd(true)}>+ Vložiť email na klasifikáciu</BtnAdd>
      </div>
      {loading ? (
        <div style={{ padding: SPACE.xl, color: COLORS.muted, textAlign: 'center' }}>Načítavam…</div>
      ) : emails.length === 0 ? (
        <div style={{ padding: SPACE.xl, color: COLORS.muted, textAlign: 'center',
          background: COLORS.bgSoft, borderRadius: RADIUS.md }}>
          Zatiaľ žiadne klasifikované emaily. Vlož prvý cez tlačidlo hore.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.xs }}>
          {emails.map(em => (
            <div key={em.id} style={{
              padding: SPACE.md, background: COLORS.bg,
              border: `1px solid ${COLORS.line}`,
              borderLeft: `4px solid ${PRIO_COLORS[em.priority] || COLORS.muted}`,
              borderRadius: RADIUS.md, opacity: em.status === 'archived' ? 0.5 : 1,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: SPACE.sm, marginBottom: 4 }}>
                <span style={{ fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.bold,
                  color: PRIO_COLORS[em.priority] || COLORS.muted,
                  textTransform: 'uppercase' }}>{em.priority}</span>
                <span style={{ fontSize: TYPO.sizes.xs, padding: '2px 8px',
                  background: COLORS.bgSoft, borderRadius: RADIUS.full }}>
                  {KAT_LABELS[em.category] || em.category}
                </span>
                <span style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted }}>od {em.from_addr || '—'}</span>
                <span style={{ flex: 1 }} />
                <span style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, fontFamily: TYPO.mono }}>
                  {em.created_at?.slice(0, 16)}
                </span>
                {em.status !== 'archived' && (
                  <button onClick={() => archive(em.id)} style={{
                    background: 'transparent', border: 'none', cursor: 'pointer',
                    color: COLORS.muted, fontSize: TYPO.sizes.sm, padding: '2px 6px',
                  }}>✕ archivovať</button>
                )}
              </div>
              <div style={{ fontSize: TYPO.sizes.md, fontWeight: TYPO.weights.semibold, marginBottom: 4 }}>
                {em.subject || '(bez predmetu)'}
              </div>
              {em.ai_reasoning && (
                <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, fontStyle: 'italic' }}>
                  AI: {em.ai_reasoning}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {showAdd && <ClassifyModal onClose={() => setShowAdd(false)} onDone={() => { setShowAdd(false); nacitaj() }} />}
    </div>
  )
}

function ClassifyModal({ onClose, onDone }) {
  const [form, setForm] = useState({ from: '', subject: '', body: '' })
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState(null)
  const toast = useToast()
  async function klasifikuj() {
    if (!form.subject && !form.body) { toast.error('Vyplň predmet alebo telo'); return }
    setBusy(true)
    try {
      const r = await fetchAuth('/api/trinity/email/classify', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      })
      const d = await r.json()
      if (!r.ok) throw new Error(d.error || 'classify failed')
      setResult(d)
    } catch (e) { toast.error('Chyba: ' + e.message) } finally { setBusy(false) }
  }
  const inp = { width: '100%', padding: '8px 10px', border: `1px solid ${COLORS.line}`,
    borderRadius: RADIUS.sm, fontSize: TYPO.sizes.sm, background: COLORS.bg,
    color: COLORS.text, boxSizing: 'border-box' }
  return (
    <Modal open title="Vlož email na klasifikáciu" onClose={onClose} width={600}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.md }}>
        <label>
          <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, marginBottom: 4 }}>Od (voliteľne)</div>
          <input type="text" value={form.from} onChange={e => setForm({...form, from: e.target.value})} style={inp} />
        </label>
        <label>
          <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, marginBottom: 4 }}>Predmet</div>
          <input type="text" value={form.subject} onChange={e => setForm({...form, subject: e.target.value})} style={inp} />
        </label>
        <label>
          <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, marginBottom: 4 }}>Telo emailu</div>
          <textarea value={form.body} onChange={e => setForm({...form, body: e.target.value})} rows={8}
            style={{ ...inp, resize: 'vertical', fontFamily: 'monospace', fontSize: TYPO.sizes.xs }} />
        </label>
        {result && (
          <div style={{ padding: SPACE.md, background: 'rgba(34,197,94,0.06)',
            border: `1px solid ${COLORS.add}`, borderRadius: RADIUS.md }}>
            <div style={{ fontWeight: TYPO.weights.bold, color: COLORS.add, marginBottom: 4 }}>
              ✓ Klasifikované
            </div>
            <div><strong>Kategória:</strong> {result.category}</div>
            <div><strong>Priorita:</strong> {result.priority}</div>
            <div style={{ marginTop: 4, fontStyle: 'italic', fontSize: TYPO.sizes.sm }}>
              {result.reasoning}
            </div>
          </div>
        )}
        <div style={{ display: 'flex', gap: SPACE.sm, justifyContent: 'flex-end' }}>
          <BtnGhost onClick={onClose} disabled={busy}>Zatvoriť</BtnGhost>
          {!result ? (
            <BtnAdd onClick={klasifikuj} disabled={busy}>{busy ? 'Klasifikujem…' : 'Klasifikovať'}</BtnAdd>
          ) : (
            <BtnAdd onClick={() => { onDone(); }}>OK</BtnAdd>
          )}
        </div>
      </div>
    </Modal>
  )
}

// ── CashReconPanel — Trinity cash rekonciliácia (pokladňa cash vs payroll cash) ──
function CashReconPanel() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [year, setYear] = useState(new Date().getFullYear())
  const [month, setMonth] = useState('')  // prázdne = celý rok
  function nacitaj() {
    setLoading(true)
    const q = month ? `?year=${year}&month=${month}` : `?year=${year}`
    fetchAuth(`/api/trinity/cash-recon${q}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => setData(d))
      .finally(() => setLoading(false))
  }
  useEffect(nacitaj, [])  // pri otvorení Trinity
  const ok = data && Math.abs(data.rozdiel || 0) < 0.5
  return (
    <div style={{
      padding: SPACE.md,
      background: data ? (ok ? 'rgba(34,197,94,0.06)' : 'rgba(239,68,68,0.08)') : COLORS.bgSoft,
      border: `1px solid ${data ? (ok ? COLORS.add : COLORS.remove) : COLORS.line}`,
      borderRadius: RADIUS.md,
      display: 'flex', flexDirection: 'column', gap: SPACE.sm,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: SPACE.md }}>
        <div style={{ fontWeight: TYPO.weights.bold, fontSize: TYPO.sizes.md, flex: 1 }}>
          Cash rekonciliácia — pokladňa vs mzdy
        </div>
        <select value={year} onChange={e => setYear(Number(e.target.value))}
          style={{ padding: '4px 8px', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.sm }}>
          {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <select value={month} onChange={e => setMonth(e.target.value)}
          style={{ padding: '4px 8px', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.sm }}>
          <option value="">Celý rok</option>
          {['01','02','03','04','05','06','07','08','09','10','11','12'].map(m =>
            <option key={m} value={m}>{m}</option>)}
        </select>
        <button onClick={nacitaj} disabled={loading} style={{
          padding: '4px 12px', background: COLORS.bg, border: `1px solid ${COLORS.line}`,
          borderRadius: RADIUS.sm, cursor: 'pointer', fontSize: TYPO.sizes.sm,
        }}>{loading ? '…' : 'Spustiť'}</button>
      </div>
      {data && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: SPACE.sm }}>
          <div style={{ padding: SPACE.sm, background: COLORS.bg, borderRadius: RADIUS.sm }}>
            <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, textTransform: 'uppercase' }}>Pokladňa — cash mzdy</div>
            <div style={{ fontSize: TYPO.sizes.md, fontFamily: TYPO.mono, fontWeight: TYPO.weights.bold }}>
              {SK.eur(data.pokladna_cash_mzdy || 0)}
            </div>
          </div>
          <div style={{ padding: SPACE.sm, background: COLORS.bg, borderRadius: RADIUS.sm }}>
            <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, textTransform: 'uppercase' }}>Mzdy — cash</div>
            <div style={{ fontSize: TYPO.sizes.md, fontFamily: TYPO.mono, fontWeight: TYPO.weights.bold }}>
              {SK.eur(data.payroll_cash || 0)}
            </div>
          </div>
          <div style={{
            padding: SPACE.sm,
            background: ok ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)',
            borderRadius: RADIUS.sm,
          }}>
            <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, textTransform: 'uppercase' }}>Rozdiel</div>
            <div style={{
              fontSize: TYPO.sizes.md, fontFamily: TYPO.mono, fontWeight: TYPO.weights.bold,
              color: ok ? COLORS.add : COLORS.remove,
            }}>{SK.eur(data.rozdiel || 0)}</div>
          </div>
        </div>
      )}
      {data && (
        <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, fontStyle: 'italic' }}>
          {ok
            ? 'Súčet hotovostných výplat z pokladne sedí so súčtom cash mzdy v module Mzdy.'
            : 'Rozdiel medzi pokladňou a mzdami — pozri či mzda alebo pohyb v pokladni nechýba, prípadne dvojzápis.'}
        </div>
      )}
    </div>
  )
}



// ── Trinity úlohy (Úroveň 3, Mirko GO 3.7.2026): osobný pracovný zoznam — plní Trinity (cez potvrdenie) aj používateľ ──
function TasksTab() {
  const [data, setData] = useState({ open: [], done: [] })
  const [title, setTitle] = useState('')
  const [kind, setKind] = useState('task')
  const [due, setDue] = useState('')
  const [waitOn, setWaitOn] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const load = () => fetchAuth('/api/trinity/tasks').then(r => r.ok ? r.json() : { open: [], done: [] }).then(d => setData(d && d.open ? d : { open: [], done: [] })).catch(() => {})
  useEffect(() => { load() }, [])
  const add = async () => {
    if (!title.trim() || busy) return
    setBusy(true); setErr('')
    try {
      const r = await fetchAuth('/api/trinity/tasks', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ title, kind, due_date: due, waiting_on: kind === 'waiting_on' ? waitOn : '' }) })
      const d = await r.json(); if (!r.ok) throw new Error(d.error || 'Chyba')
      setTitle(''); setDue(''); setWaitOn(''); load()
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }
  const setStatus = async (id, status) => {
    try { await fetchAuth('/api/trinity/tasks/' + id, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status }) }); load() } catch (_) {}
  }
  const today = todayLocal()
  const dnes = data.open.filter(t => t.kind !== 'waiting_on' && t.due_date && t.due_date <= today)
  const cakam = data.open.filter(t => t.kind === 'waiting_on')
  const neskor = data.open.filter(t => !dnes.includes(t) && !cakam.includes(t))
  const inp = { border: '1px solid ' + COLORS.line, borderRadius: 8, padding: '8px 10px', fontSize: TYPO.sizes.sm, fontFamily: 'inherit' }
  const Riadok = ({ t, doneRow }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderBottom: '1px solid ' + COLORS.line, background: doneRow ? '#fafaf8' : '#fff', opacity: doneRow ? 0.65 : 1 }}>
      <input type="checkbox" checked={!!doneRow} onChange={() => setStatus(t.id, doneRow ? 'open' : 'done')} title={doneRow ? 'Vrátiť medzi otvorené' : 'Označiť hotové'} style={{ width: 16, height: 16, accentColor: '#16a34a', cursor: 'pointer', flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: TYPO.sizes.sm, fontWeight: 600, textDecoration: doneRow ? 'line-through' : 'none' }}>{t.title}</div>
        <div style={{ fontSize: 11, color: COLORS.muted, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {t.kind === 'waiting_on' && <span style={{ color: '#b45309' }}>čakám na: {t.waiting_on || '?'}</span>}
          {t.kind === 'reminder' && <span>pripomienka</span>}
          {t.due_date && <span style={{ color: !doneRow && t.due_date < today ? '#dc2626' : undefined, fontWeight: !doneRow && t.due_date < today ? 700 : 400 }}>{!doneRow && t.due_date < today ? '' : ''}termín {SK.date ? SK.date(t.due_date) : t.due_date}</span>}
          {t.link_type && <span>{t.link_type}{t.link_id ? ': ' + t.link_id : ''}</span>}
          {t.note && <span>· {t.note}</span>}
          {t.source === 'trinity' && <span style={{ color: '#7c3aed' }}>od Trinity</span>}
        </div>
      </div>
      {!doneRow && <button onClick={() => setStatus(t.id, 'cancelled')} title="Zrušiť úlohu" style={{ border: 'none', background: 'none', color: COLORS.muted, cursor: 'pointer', fontSize: 13 }}>✕</button>}
    </div>
  )
  const Sekcia = ({ label, rows, farba, prazdne }) => (
    <div style={{ marginBottom: SPACE.md }}>
      <div style={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', color: farba || COLORS.muted, letterSpacing: '0.04em', margin: '0 0 6px 2px' }}>{label} ({rows.length})</div>
      <div style={{ border: '1px solid ' + COLORS.line, borderRadius: 10, overflow: 'hidden' }}>
        {rows.length ? rows.map(t => <Riadok key={t.id} t={t} />) : <div style={{ padding: '10px 12px', fontSize: 12, color: COLORS.muted }}>{prazdne}</div>}
      </div>
    </div>
  )
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: SPACE.md, flexWrap: 'wrap', alignItems: 'center' }}>
        <input value={title} onChange={e => setTitle(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') add() }} placeholder="Nová úloha… (alebo povedz Trinity v chate a potvrď kartou)" style={{ ...inp, flex: 1, minWidth: 220 }} />
        <select value={kind} onChange={e => setKind(e.target.value)} style={inp}>
          <option value="task">Úloha</option><option value="waiting_on">Čakám na</option><option value="reminder">Pripomienka</option>
        </select>
        {kind === 'waiting_on' && <input value={waitOn} onChange={e => setWaitOn(e.target.value)} placeholder="od koho" style={{ ...inp, width: 130 }} />}
        <input type="date" value={due} onChange={e => setDue(e.target.value)} style={inp} />
        <button onClick={add} disabled={busy || !title.trim()} style={{ border: 'none', background: COLORS.brand, color: '#fff', borderRadius: 8, padding: '9px 16px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', opacity: (busy || !title.trim()) ? 0.5 : 1 }}>Pridať</button>
      </div>
      {err && <div style={{ color: COLORS.remove, fontSize: 12, marginBottom: 8 }}>{err}</div>}
      <Sekcia label="Dnes / prešvihnuté" rows={dnes} farba="#dc2626" prazdne="Nič naliehavé — dobré ráno." />
      <Sekcia label="Čakám na" rows={cakam} farba="#b45309" prazdne="Na nikoho nečakáš." />
      <Sekcia label="Neskôr / bez termínu" rows={neskor} prazdne="Prázdno." />
      {data.done.length > 0 && (
        <div style={{ marginBottom: SPACE.md }}>
          <div style={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', color: '#16a34a', letterSpacing: '0.04em', margin: '0 0 6px 2px' }}>Hotové (posledných {data.done.length})</div>
          <div style={{ border: '1px solid ' + COLORS.line, borderRadius: 10, overflow: 'hidden' }}>
            {data.done.map(t => <Riadok key={t.id} t={t} doneRow />)}
          </div>
        </div>
      )}
    </div>
  )
}

// ── Trinity asistent (Claude nad živými dátami, Mirko 2026-06-28: Trinity = Claude, jeden asistent) ──
function renderMd(text) {
  return String(text).split('\n').map((line, i) => {
    const isH = /^#{1,3}\s/.test(line)
    const l = line.replace(/^#{1,3}\s/, '')
    const parts = l.split(/(\*\*[^*]+\*\*)/g).map((p, j) => /^\*\*[^*]+\*\*$/.test(p) ? <b key={j}>{p.slice(2, -2)}</b> : p)
    return <div key={i} style={{ fontWeight: isH ? 800 : 400, marginTop: isH ? 8 : 0, fontSize: isH ? 14 : undefined }}>{parts.length ? parts : '\u00A0'}</div>
  })
}

// Anton v karte Trinity (Mirko 12. 9. 2026: „zmeň to ako sme urobili Antona, kým si kúpim kredit na Anthropic").
// Ten istý Anton ako v plávajúcom okne: /anton/ cez bránu, prihlásenie zo zdieľaného kamir_token. Starý TrinityChat ostáva v kóde, nepoužíva sa.
function AntonFrame() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 230px)', minHeight: 480, border: '1px solid ' + COLORS.line, borderRadius: 12, overflow: 'hidden', background: '#fff' }}>
      <div style={{ padding: '6px 12px', fontSize: 12, color: COLORS.muted, borderBottom: '1px solid ' + COLORS.line, display: 'flex', justifyContent: 'space-between', gap: 8 }}>
        <span>Trinity, motor Anton. Pomôže, poradí, vysvetlí. Nič nemení. Vlákna si pamätá.</span>
        <a href="/anton/" style={{ color: COLORS.nav, textDecoration: 'underline' }} title="Otvoriť Antona na celú stránku (vlákna, zápisník, nastavenia)">Celá stránka</a>
      </div>
      <iframe title="Trinity (Anton)" src="/anton/" style={{ flex: 1, width: '100%', border: 'none' }} allow="clipboard-write" />
    </div>
  )
}

export function TrinityChat({ compact = false, autoLast = false } = {}) {
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [sending, setSending] = useState(false)
  const [atts, setAtts] = useState([])     // prílohy: [{id,kind:'image'|'pdf',name,mediaType,base64,dataUrl}]
  const [drag, setDrag] = useState(false)
  const [note, setNote] = useState('')
  const scrollRef = React.useRef(null)
  const fileRef = React.useRef(null)
  React.useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight }, [messages, sending, atts])

  // Úroveň 2: pamäť konverzácií + denný súhrn
  const [threadId, setThreadId] = useState(null)
  const [threads, setThreads] = useState([])
  const [showHist, setShowHist] = useState(false)
  const [digest, setDigest] = useState(null)
  const [digestOpen, setDigestOpen] = useState(false)
  const [digestBusy, setDigestBusy] = useState(false)
  const loadThreads = () => fetchAuth('/api/trinity/threads').then(r => r.ok ? r.json() : []).then(d => setThreads(Array.isArray(d) ? d : [])).catch(() => {})
  React.useEffect(() => { loadThreads(); if (autoLast) fetchAuth('/api/trinity/threads').then(r => r.ok ? r.json() : []).then(d => { const t = Array.isArray(d) ? d.slice().sort((a, b) => String(b.updated_at || '').localeCompare(String(a.updated_at || '')))[0] : null; if (t) openThread(t.id) }).catch(() => {}); fetchAuth('/api/trinity/digest/today').then(r => r.ok ? r.json() : null).then(d => setDigest(d && d.content ? d : null)).catch(() => {}) }, [])
  const openThread = async (id) => {
    try {
      const r = await fetchAuth('/api/trinity/threads/' + id); const d = await r.json()
      if (!r.ok) throw new Error(d.error || 'Chyba')
      setMessages((d.messages || []).map(m => ({ role: m.role, content: m.content })))
      setThreadId(id); setShowHist(false)
    } catch (e) { setNote('História: ' + e.message) }
  }
  const genDigest = async () => {
    if (digestBusy) return
    setDigestBusy(true)
    try { const r = await fetchAuth('/api/trinity/digest/generate', { method: 'POST' }); const d = await r.json(); if (!r.ok) throw new Error(d.error || 'Chyba'); setDigest(d); setDigestOpen(true) }
    catch (e) { setNote('Súhrn: ' + e.message) } finally { setDigestBusy(false) }
  }
  // Úroveň 3: nevyriešené akcie z minula (po reloade) — zobraz kartu hore
  React.useEffect(() => {
    fetchAuth('/api/trinity/actions?status=pending').then(r => r.ok ? r.json() : []).then(d => {
      if (Array.isArray(d) && d.length) setMessages(m => m.length ? m : [{ role: 'actions', items: d.map(a => ({ id: a.id, tool_name: a.tool_name, preview: a.preview, status: 'pending' })) }])
    }).catch(() => {})
  }, [])
  const resolveAction = async (msgIdx, actionId, verdict) => {
    try {
      const r = await fetchAuth(`/api/trinity/actions/${actionId}/${verdict}`, { method: 'POST' })
      const d = await r.json()
      if (!r.ok) throw new Error(d.error || 'Chyba')
      setMessages(m => m.map((msg, i) => i === msgIdx && msg.role === 'actions' ? { ...msg, items: msg.items.map(a => a.id === actionId ? { ...a, status: verdict === 'approve' ? 'executed' : 'rejected' } : a) } : msg))
    } catch (e) { setNote('Akcia: ' + e.message) }
  }

  // Obrázok zmenší na max 1568 px / JPEG 0.82 (šetrí prenos aj tokeny); PDF nechá. HEIC/iné = jasná hláška.
  const fileToAtt = (file) => new Promise((resolve) => {
    const type = (file.type || '').toLowerCase()
    const rid = Math.random().toString(36).slice(2)
    if (type === 'application/pdf') {
      const r = new FileReader()
      r.onload = () => resolve({ id: rid, kind: 'pdf', name: file.name || 'dokument.pdf', mediaType: 'application/pdf', base64: String(r.result).split(',')[1] || '' })
      r.onerror = () => resolve(null); r.readAsDataURL(file); return
    }
    if (!/^image\/(jpeg|png|gif|webp)$/.test(type)) { resolve({ err: file.name || 'súbor', reason: type.includes('heic') ? 'HEIC nepodporované — odfoť/ulož ako JPEG' : 'nepodporovaný formát (len JPG/PNG/GIF/WEBP/PDF)' }); return }
    const r = new FileReader()
    r.onload = () => {
      const img = new Image()
      img.onload = () => {
        const max = 1568; let w = img.width, h = img.height
        if (w > max || h > max) { const s = max / Math.max(w, h); w = Math.round(w * s); h = Math.round(h * s) }
        const c = document.createElement('canvas'); c.width = w; c.height = h
        c.getContext('2d').drawImage(img, 0, 0, w, h)
        const dataUrl = c.toDataURL('image/jpeg', 0.82)
        resolve({ id: rid, kind: 'image', name: file.name || 'obrázok', mediaType: 'image/jpeg', base64: dataUrl.split(',')[1] || '', dataUrl })
      }
      img.onerror = () => resolve(null); img.src = String(r.result)
    }
    r.onerror = () => resolve(null); r.readAsDataURL(file)
  })

  const addFiles = async (fileList) => {
    const files = Array.from(fileList || []).slice(0, 6)
    if (!files.length) return
    const res = await Promise.all(files.map(fileToAtt))
    const ok = res.filter(x => x && !x.err)
    const errs = res.filter(x => x && x.err)
    if (ok.length) setAtts(a => [...a, ...ok].slice(0, 6))
    setNote(errs.length ? errs.map(e => e.err + ': ' + e.reason).join(' · ') : '')
  }

  const send = async (q) => {
    const question = (q != null ? q : input).trim()
    if ((!question && !atts.length) || sending) return
    setInput(''); setNote('')
    let content
    if (atts.length) {
      content = [
        { type: 'text', text: question || 'Pozri prílohu a povedz čo je dôležité.' },
        ...atts.map(a => a.kind === 'image'
          ? { type: 'image', source: { type: 'base64', media_type: a.mediaType, data: a.base64 } }
          : { type: 'document', source: { type: 'base64', media_type: 'application/pdf', data: a.base64 } }),
      ]
    } else content = question
    const userMsg = { role: 'user', content, atts: atts.filter(a => a.kind === 'pdf').map(a => ({ kind: 'pdf', name: a.name })) }
    const next = [...messages, userMsg]
    setMessages(next); setAtts([]); setSending(true)
    try {
      const r = await fetchAuth('/api/trinity/chat', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ thread_id: threadId || undefined, messages: next.filter(m => m.role === 'user' || m.role === 'assistant').map(m => ({ role: m.role, content: m.content })) }) })
      const d = await r.json()
      if (!r.ok) throw new Error(d.error || 'Chyba')
      setMessages(m => [...m, { role: 'assistant', content: d.answer }, ...(Array.isArray(d.actions) && d.actions.length ? [{ role: 'actions', items: d.actions }] : [])])
      if (d.thread_id && !threadId) { setThreadId(d.thread_id); loadThreads() }
    } catch (e) { setMessages(m => [...m, { role: 'assistant', content: 'Chyba: ' + e.message }]) } finally { setSending(false) }
  }

  const chips = ['Aké pohľadávky mám po splatnosti?', 'Ktoré zákazky nečerpajú alebo sú prečerpané?', 'Ako funguje Súpis prác?', 'Čo mám dnes riešiť ako prvé?']

  const renderUser = (m) => {
    if (!Array.isArray(m.content)) return m.content
    const text = m.content.filter(b => b.type === 'text').map(b => b.text).join(' ')
    const imgs = m.content.filter(b => b.type === 'image')
    const docs = m.atts || []
    return (
      <div>
        {(imgs.length > 0 || docs.length > 0) && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: text ? 6 : 0 }}>
            {imgs.map((b, j) => <img key={'i' + j} src={`data:${b.source.media_type};base64,${b.source.data}`} alt="" style={{ width: 92, height: 92, objectFit: 'cover', borderRadius: 8, border: '1px solid rgba(255,255,255,.3)' }} />)}
            {docs.map((a, j) => <span key={'d' + j} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'rgba(255,255,255,.18)', borderRadius: 8, padding: '5px 9px', fontSize: 12 }}><NavIcon name="doks" size={13} />{a.name}</span>)}
          </div>
        )}
        {text && text !== 'Pozri prílohu a povedz čo je dôležité.' && <div>{text}</div>}
      </div>
    )
  }

  return (
    <div onDragOver={e => { e.preventDefault(); setDrag(true) }} onDragLeave={() => setDrag(false)} onDrop={e => { e.preventDefault(); setDrag(false); addFiles(e.dataTransfer.files) }}
      style={{ display: 'flex', flexDirection: 'column', height: compact ? '100%' : 'calc(100vh - 250px)', minHeight: compact ? 0 : 420, position: 'relative', outline: drag ? '2px dashed ' + COLORS.brand : 'none', borderRadius: 12 }}>
      {drag && <div style={{ position: 'absolute', inset: 0, background: 'rgba(216,90,48,.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 5, color: COLORS.brand, fontWeight: 700, pointerEvents: 'none' }}>Pusti obrázok / PDF sem</div>}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '0 2px 6px', flexWrap: 'wrap' }}>
        <button onClick={() => { setMessages([]); setThreadId(null); setShowHist(false) }} title="Začať novú konverzáciu (stará ostane v histórii)" style={{ border: '1px solid ' + COLORS.line, background: '#fff', borderRadius: 8, padding: '5px 11px', fontSize: 12, fontWeight: 700, cursor: 'pointer', color: COLORS.text, fontFamily: 'inherit' }}>+ Nová konverzácia</button>
        <button onClick={() => { setShowHist(s => !s); loadThreads() }} title="Uložené konverzácie — klik otvorí a pokračuješ kde si skončil" style={{ border: '1px solid ' + COLORS.line, background: showHist ? '#f5f6ff' : '#fff', borderRadius: 8, padding: '5px 11px', fontSize: 12, fontWeight: 700, cursor: 'pointer', color: COLORS.text, fontFamily: 'inherit' }}>História{threads.length ? ' (' + threads.length + ')' : ''}</button>
        <button onClick={() => digest ? setDigestOpen(o => !o) : genDigest()} disabled={digestBusy} title="Ranný súhrn zmien za 24h — generuje sa automaticky každé ráno" style={{ border: '1px solid #f2d9a4', background: digestOpen ? '#fff3d6' : '#fffaf0', borderRadius: 8, padding: '5px 11px', fontSize: 12, fontWeight: 700, cursor: 'pointer', color: '#92400e', fontFamily: 'inherit', opacity: digestBusy ? 0.6 : 1 }}>{digestBusy ? 'Generujem…' : 'Denný súhrn'}</button>
      </div>
      {showHist && (
        <div style={{ maxHeight: 190, overflow: 'auto', border: '1px solid ' + COLORS.line, borderRadius: 10, marginBottom: 8, background: '#fff' }}>
          {!threads.length && <div style={{ padding: 10, color: COLORS.muted, fontSize: 12 }}>Zatiaľ žiadne uložené konverzácie — každý nový chat sa odteraz ukladá sám.</div>}
          {threads.map(t => (
            <div key={t.id} onClick={() => openThread(t.id)} style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid ' + COLORS.line, fontSize: 13, display: 'flex', justifyContent: 'space-between', gap: 8, background: t.id === threadId ? '#f5f6ff' : undefined }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.title || 'Konverzácia ' + t.id}</span>
              <span style={{ color: COLORS.muted, fontSize: 11, flexShrink: 0 }}>{String(t.updated_at || '').slice(0, 16)} · {t.n} správ</span>
            </div>
          ))}
        </div>
      )}
      {digestOpen && digest && (
        <div style={{ border: '1px solid #f2d9a4', background: '#fffaf0', borderRadius: 10, padding: '10px 14px', marginBottom: 8, fontSize: TYPO.sizes.sm, lineHeight: 1.5, maxHeight: 250, overflow: 'auto' }}>
          <div style={{ fontWeight: 800, marginBottom: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Denný súhrn — {digest.date}</span>
            <button onClick={genDigest} disabled={digestBusy} title="Pregenerovať z aktuálnych dát" style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 14, color: '#92400e' }}>↻</button>
          </div>
          {renderMd(digest.content)}
        </div>
      )}
      <div ref={scrollRef} style={{ flex: 1, overflow: 'auto', padding: '6px 2px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {!messages.length && (
          <div style={{ color: COLORS.muted, padding: '18px 8px', textAlign: 'center' }}>
            <div style={{ fontWeight: 800, color: COLORS.text, marginBottom: 6, fontSize: TYPO.sizes.md }}>Trinity — tvoj firemný asistent</div>
            <div style={{ fontSize: TYPO.sizes.sm }}>Spýtaj sa o financiách, zákazkách, pohľadávkach, čerpaní, alebo „ako funguje X". Vidím živé dáta + príručku Matrixu. Môžeš pridať aj <b>obrázok/PDF</b> (doklad, screenshot, foto).</div>
          </div>
        )}
        {messages.map((m, i) => m.role === 'actions' ? (
          <div key={i} style={{ alignSelf: 'flex-start', maxWidth: '88%', display: 'flex', flexDirection: 'column', gap: 6 }}>
            {m.items.map(a => (
              <div key={a.id} style={{ border: '1.5px solid ' + (a.status === 'executed' ? '#86efac' : a.status === 'rejected' ? '#fecaca' : '#fcd34d'), background: a.status === 'executed' ? '#f0fdf4' : a.status === 'rejected' ? '#fef2f2' : '#fffbeb', borderRadius: 12, padding: '10px 14px', fontSize: TYPO.sizes.sm }}>
                <div style={{ fontWeight: 700, marginBottom: a.status === 'pending' ? 8 : 0, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span>{a.status === 'executed' ? 'Vykonané' : a.status === 'rejected' ? 'Zamietnuté' : 'Čaká'}</span>
                  <span>{a.preview}</span>
                </div>
                {a.status === 'pending' && (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => resolveAction(i, a.id, 'approve')} style={{ border: 'none', background: '#16a34a', color: '#fff', borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Vykonať</button>
                    <button onClick={() => resolveAction(i, a.id, 'reject')} style={{ border: '1px solid #fca5a5', background: '#fff', color: '#b91c1c', borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Zamietnuť</button>
                  </div>
                )}
                {a.status === 'executed' && <div style={{ fontSize: 11, color: '#15803d', marginTop: 2 }}>Vykonané — nájdeš v karte Úlohy / pamäti Trinity.</div>}
              </div>
            ))}
          </div>
        ) : (
          <div key={i} style={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '88%', background: m.role === 'user' ? COLORS.nav : '#faf9f6', color: m.role === 'user' ? '#fff' : COLORS.text, border: m.role === 'user' ? 'none' : ('1px solid ' + COLORS.line), borderRadius: 12, padding: '10px 14px', fontSize: TYPO.sizes.sm, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
            {m.role === 'assistant' ? renderMd(m.content) : renderUser(m)}
          </div>
        ))}
        {sending && <div style={{ alignSelf: 'flex-start', color: COLORS.muted, fontSize: TYPO.sizes.sm, padding: '6px 10px' }}>Trinity premýšľa…</div>}
      </div>
      {!messages.length && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', padding: '8px 2px' }}>
          {chips.map(c => <button key={c} onClick={() => send(c)} style={{ border: '1px solid ' + COLORS.line, background: '#fff', borderRadius: 20, padding: '5px 12px', fontSize: 12, cursor: 'pointer', color: COLORS.text, fontFamily: 'inherit' }}>{c}</button>)}
        </div>
      )}
      {atts.length > 0 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', padding: '6px 2px' }}>
          {atts.map(a => (
            <div key={a.id} style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 5, background: '#f1efe8', borderRadius: 8, padding: a.kind === 'image' ? 0 : '8px 10px', fontSize: 12 }}>
              {a.kind === 'image' ? <img src={a.dataUrl} alt="" style={{ width: 54, height: 54, objectFit: 'cover', borderRadius: 8 }} /> : <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><NavIcon name="doks" size={13} />{a.name}</span>}
              <button onClick={() => setAtts(x => x.filter(y => y.id !== a.id))} title="Odobrať" style={{ position: a.kind === 'image' ? 'absolute' : 'static', top: -7, right: -7, border: '2px solid #fff', background: COLORS.remove, color: '#fff', borderRadius: '50%', width: 20, height: 20, cursor: 'pointer', fontSize: 11, lineHeight: 1, fontFamily: 'inherit', padding: 0 }}>✕</button>
            </div>
          ))}
        </div>
      )}
      {note && <div style={{ color: COLORS.remove, fontSize: 12, padding: '2px 4px' }}>{note}</div>}
      <div style={{ display: 'flex', gap: 8, padding: '8px 2px', borderTop: '1px solid ' + COLORS.line, alignItems: 'center' }}>
        <input ref={fileRef} type="file" accept="image/*,.pdf,application/pdf" multiple onChange={e => { addFiles(e.target.files); e.target.value = '' }} style={{ display: 'none' }} />
        <button onClick={() => fileRef.current && fileRef.current.click()} title="Pridať obrázok / PDF" style={{ border: '1px solid ' + COLORS.line, background: '#fff', borderRadius: 10, width: 40, height: 40, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: COLORS.muted, flexShrink: 0 }}><NavIcon name="doks" size={18} /></button>
        <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') send() }} onPaste={e => { const imgs = Array.from((e.clipboardData && e.clipboardData.items) || []).filter(it => it.type && it.type.startsWith('image/')).map(it => it.getAsFile()).filter(Boolean); if (imgs.length) { e.preventDefault(); addFiles(imgs) } }} placeholder="Spýtaj sa Trinity… (alebo vlož/pretiahni obrázok)" style={{ flex: 1, padding: '10px 12px', border: '1px solid ' + COLORS.line, borderRadius: 10, fontSize: TYPO.sizes.sm, fontFamily: 'inherit', outline: 'none' }} />
        <button onClick={() => send()} disabled={sending || (!input.trim() && !atts.length)} style={{ border: 'none', background: COLORS.brand, color: '#fff', borderRadius: 10, padding: '0 18px', height: 40, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', opacity: (sending || (!input.trim() && !atts.length)) ? 0.5 : 1 }}>Poslať</button>
      </div>
    </div>
  )
}
